import { useEffect, useState } from 'react'
import { Alert, Box, CircularProgress, Dialog, DialogContent, List, ListItemButton, ListItemText, Snackbar, Typography, } from '@mui/material'
import { useGetLojaAtualQuery, useChamarGarcomTabletMutation } from '../api'
import { contextoSalao } from '../lib/salao'
import { destinoChamadoConfigurado } from '../lib/destino-chamado'
import { onAbrirChamarGarcom } from '../lib/tablet-eventos'
import { DialogTitle } from './dialog-componentes'

const OPCOES_PADRAO = ['Fechar a conta', 'Pedir a conta', 'Trazer água', 'Falar com o garçom']
const COOLDOWN_MS = 45_000

export function ChamarGarcom(): JSX.Element | null {
  const { data: loja } = useGetLojaAtualQuery()
  const [aberto, setAberto] = useState(false)
  const [emCooldown, setEmCooldown] = useState(false)
  const [feedback, setFeedback] = useState<{ tipo: 'success' | 'error'; msg: string } | null>(null)
  const [chamar, status] = useChamarGarcomTabletMutation()
  const ctx = contextoSalao(loja)

  // Antes do return: o polling da loja liga e desliga o chamado com a tela aberta, e hook depois dele derruba o tablet.
  useEffect(() => onAbrirChamarGarcom(() => setAberto(true)), [])

  if (!loja || ctx.mesaFisica == null || !destinoChamadoConfigurado(loja)) {
    return null
  }

  const opcoesConfig = loja.configuracao.tabletChamarGarcomOpcoes
  const opcoes = opcoesConfig && opcoesConfig.length > 0 ? opcoesConfig : OPCOES_PADRAO
  const identificacao = `Mesa ${ctx.mesaFisica}`

  async function escolher(opcao: string): Promise<void> {
    try {
      await chamar({ identificacao, opcao }).unwrap()

      setFeedback({ tipo: 'success', msg: 'Garçom chamado!' })
      setEmCooldown(true)
      setTimeout(() => setEmCooldown(false), COOLDOWN_MS)
    } catch (e) {
      const motivo = (e as { data?: { motivo?: string } })?.data?.motivo
      setFeedback({
        tipo: 'error',
        msg:
          motivo === 'sem_local_impressao'
            ? 'Chamar garçom não está configurado na loja. Avise o gerente.'
            : 'Não foi possível chamar o garçom. Tente novamente.',
      })
    } finally {
      setAberto(false)
    }
  }

  return (
    <>
      <Dialog open={aberto} onClose={() => setAberto(false)} fullWidth maxWidth="xs">
        <DialogTitle onClose={() => setAberto(false)}>
          <Typography variant='h5'>
            Chamar garçom
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          {emCooldown && (
            <Alert severity="success" sx={{ mb: 1 }}>
              Garçom chamado! Aguarde alguns segundos para chamar novamente.
            </Alert>
          )}

          <List sx={{ p: 0 }}>
            {opcoes.map((op) => (
              <ListItemButton
                key={op}
                onClick={() => void escolher(op)}
                disabled={status.isLoading || emCooldown}
                sx={{ px: 2, py: 1 }}
              >
                <ListItemText primary={`${opcoes.indexOf(op) + 1} - ${op}`} />
              </ListItemButton>
            ))}
          </List>

          {status.isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {feedback && (
        <Snackbar
          open
          autoHideDuration={3000}
          onClose={() => setFeedback(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={feedback.tipo} variant="filled" onClose={() => setFeedback(null)}>
            {feedback.msg}
          </Alert>
        </Snackbar>
      )}
    </>
  )
}
